import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createChart, ColorType, CrosshairMode, CandlestickSeries, LineSeries } from 'lightweight-charts';
import {
  computeSessionScopedAutoFib,
  sessionFibSignature,
  formatChartDateTime,
  formatChartTickMark,
  collectSessionOpenAxisMarks,
  sessionOpenTimeSet,
  fibLevelsFromHighLow,
  applyUserFibHighLow,
  AUTO_FIB_COLORS,
} from '../../utils/eodChartMath';
import { applySeriesPoints, toLwcPoint } from '../../utils/ohlcBarEngine';
import { getChartSyncGroup } from '../../utils/chartSyncGroup';
import {
  CHART_INTERACTION_OPTS,
  CHART_TIME_SCALE_OPTS,
  attachChartInteraction,
} from '../../utils/chartInteraction';
import DataSourceBadge from '../DataSourceBadge';
import HLineToolbar from './HLineToolbar';

/** Read a CSS variable from :root, with a fallback. */
function getCSSVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch (_) { return fallback; }
}

function rangesEqual(a, b) {
  if (!a || !b) return false;
  return a.from === b.from && a.to === b.to;
}

function loadUserHlines(chartId) {
  try {
    const raw = localStorage.getItem(`tmr_board_hlines_${chartId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      return parsed.map(p => {
        if (typeof p === 'number' && isFinite(p)) {
          return { id: 'hl_' + Math.random().toString(36).substr(2, 9), price: p, color: '#3B82F6', style: 'solid', label: '' };
        }
        return p;
      }).filter(p => p && typeof p.price === 'number');
    }
    return [];
  } catch (_) {
    return [];
  }
}

/** User-dragged Auto Fib override (per chart + timeframe). */
function fibOverrideKey(chartId, tf) {
  return `tmr_board_autofib_${chartId}_${tf || 'default'}`;
}

function loadFibOverride(chartId, tf) {
  try {
    const raw = localStorage.getItem(fibOverrideKey(chartId, tf));
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o.hi !== 'number' || typeof o.lo !== 'number' || !o.dayKey) return null;
    if (!isFinite(o.hi) || !isFinite(o.lo) || o.hi === o.lo) return null;
    return {
      dayKey: String(o.dayKey),
      hi: +Number(o.hi).toFixed(2),
      lo: +Number(o.lo).toFixed(2),
    };
  } catch (_) {
    return null;
  }
}

function saveFibOverride(chartId, tf, override) {
  try {
    if (!override) {
      localStorage.removeItem(fibOverrideKey(chartId, tf));
      return;
    }
    localStorage.setItem(fibOverrideKey(chartId, tf), JSON.stringify(override));
  } catch (_) {}
}

const FIB_HIT_PX = 10;
const FIB_HANDLE_PX = 14;

/**
 * Compact OHLC candlestick — supports realtime series.update() without resetting zoom.
 *
 * Linked zoom/pan:
 *   Pass `syncGroupId` (e.g. "idx-board-NSE") so all charts in the same group
 *   share one visible time window via chartSyncGroup (event-based, no React lag).
 *   Legacy React props `logicalRange` / `onLogicalRangeChange` still work.
 */
export default function EodMiniCandleChart({
  data = [],
  label = '',
  height = 280,
  fillHeight = false,
  intraday = false,
  live = false,
  /** Resolved data-source meta from resolveDataSource() — shown on header far-right */
  dataSource = null,
  logicalRange = null,
  onLogicalRangeChange = null,
  /** When set, join this linked-chart group for synchronized zoom/pan. */
  syncGroupId = null,
  chartId = 'mini',
  showFib = true,
  /** Selected board timeframe (e.g. '5m') — used for fib override storage + TF change. */
  tf = '5m',
  /** Minutes of the selected TF — AutoFib uses first completed candle of this size. */
  intervalMinutes = 5,
  user = null,
}) {
  const containerRef = useRef(null);
  const chartBoxRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  /** @type {React.MutableRefObject<Map<string, import('lightweight-charts').ISeriesApi<'Line'>>>} */
  const fibSeriesMapRef = useRef(new Map());
  const fibPriceLineRefs = useRef([]); // EOD (non-intraday) price-line fibs only
  const userHlineRefs = useRef([]);
  const fibSigRef = useRef('');
  const fibLevelsRef = useRef([]);
  /** Latest auto+override sessions for hit-testing / interaction. */
  const fibSessionsRef = useRef([]);
  /** Active (latest) session dayKey for interactive override. */
  const fibActiveDayRef = useRef(null);
  /** User override `{ dayKey, hi, lo }` — survives live ticks until reset / TF change. */
  const fibUserOverrideRef = useRef(loadFibOverride(chartId, tf));
  const fibSelectedRef = useRef(false);
  const fibDragRef = useRef(null); // { mode, startY, startHi, startLo, pointerId }
  const fibOverlayRef = useRef(null);
  const fibHandlesRef = useRef(null);
  /** Session-open times for smart X-axis (ref so tickMarkFormatter stays stable). */
  const sessionOpenTimesRef = useRef(new Set());
  const sessionAxisMarksRef = useRef([]);
  const sessionAxisOverlayRef = useRef(null);
  const hlineDrawRef = useRef(false);
  const syncingRef = useRef(false);
  /** Mutable series-apply state for LWC-compliant incremental updates */
  const seriesStateRef = useRef({ len: 0, lastTime: null, lastPoint: null, didFit: false });
  const onRangeChangeRef = useRef(onLogicalRangeChange);
  const syncGroupIdRef = useRef(syncGroupId);
  const chartIdRef = useRef(chartId);
  const dataRef = useRef(data);
  const intervalMinutesRef = useRef(intervalMinutes);
  const tfRef = useRef(tf);
  const [header, setHeader] = useState({ last: 0, chg: 0, chgP: '0.00', bull: true });
  const [chartError, setChartError] = useState(null);
  const [chartEpoch, setChartEpoch] = useState(0);
  const [fibLevels, setFibLevels] = useState([]);
  const [fibSelected, setFibSelected] = useState(false);
  const [fibUserAdjusted, setFibUserAdjusted] = useState(() => !!loadFibOverride(chartId, tf));
  /** Session date labels rendered under the time axis (one per trading day). */
  const [sessionAxisMarks, setSessionAxisMarks] = useState([]);
  const [hlineDraw, setHlineDraw] = useState(false);
  const [userHlines, setUserHlines] = useState(() => loadUserHlines(chartId));
  const [sharedHlines, setSharedHlines] = useState([]);
  const [selectedLineId, setSelectedLineId] = useState(null);

  const allLines = useMemo(() => [...userHlines, ...sharedHlines], [userHlines, sharedHlines]);
  const userHlinesRef = useRef(userHlines);
  const allLinesRef = useRef(allLines);
  
  useEffect(() => {
    let active = true;
    const fetchShared = async () => {
      try {
        const r = await fetch(`/api/api_shared_hlines.php?action=list&chart_id=${chartId}`);
        const data = await r.json();
        if (active && data.data) {
          setSharedHlines(data.data.map(l => ({ ...l, isShared: true, price: parseFloat(l.price) })));
        }
      } catch (e) {}
    };
    fetchShared();
    const intv = setInterval(fetchShared, 10000);
    return () => { active = false; clearInterval(intv); };
  }, [chartId]);

  useEffect(() => { userHlinesRef.current = userHlines; }, [userHlines]);
  useEffect(() => { allLinesRef.current = allLines; }, [allLines]);
  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { intervalMinutesRef.current = intervalMinutes; }, [intervalMinutes]);
  useEffect(() => { tfRef.current = tf; }, [tf]);
  useEffect(() => { fibSelectedRef.current = fibSelected; }, [fibSelected]);

  // TF / chart change → load override for this chart+tf (or auto)
  useEffect(() => {
    const o = loadFibOverride(chartId, tf);
    fibUserOverrideRef.current = o;
    setFibUserAdjusted(!!o);
    setFibSelected(false);
    fibSigRef.current = ''; // force redraw with new TF first-candle
  }, [chartId, tf]);

  const positionFibLabels = useCallback(() => {
    const series = seriesRef.current;
    const overlay = fibOverlayRef.current;
    if (series && overlay && fibLevelsRef.current.length) {
      const labels = overlay.querySelectorAll('.ic-mini-fib-lbl');
      fibLevelsRef.current.forEach((lv, i) => {
        const el = labels[i];
        if (!el) return;
        const y = series.priceToCoordinate(lv.price);
        if (y == null || y < 0) {
          el.style.visibility = 'hidden';
          return;
        }
        el.style.visibility = 'visible';
        el.style.top = `${Math.round(y)}px`;
      });
    }

    // Position stretch handles via DOM (no React re-render on pan/zoom)
    const handles = fibHandlesRef.current;
    if (!handles || !series) return;
    const active = fibSessionsRef.current.length
      ? fibSessionsRef.current[fibSessionsRef.current.length - 1]
      : null;
    const hiEl = handles.querySelector('.ic-fib-handle-hi');
    const loEl = handles.querySelector('.ic-fib-handle-lo');
    const midEl = handles.querySelector('.ic-fib-handle-mid');
    if (!active || !isFinite(active.hi) || !isFinite(active.lo)) {
      [hiEl, loEl, midEl].forEach((el) => { if (el) el.style.visibility = 'hidden'; });
      return;
    }
    const hiY = series.priceToCoordinate(active.hi);
    const loY = series.priceToCoordinate(active.lo);
    if (hiEl) {
      if (hiY == null) hiEl.style.visibility = 'hidden';
      else {
        hiEl.style.visibility = 'visible';
        hiEl.style.top = `${Math.round(hiY)}px`;
      }
    }
    if (loEl) {
      if (loY == null) loEl.style.visibility = 'hidden';
      else {
        loEl.style.visibility = 'visible';
        loEl.style.top = `${Math.round(loY)}px`;
      }
    }
    if (midEl) {
      if (hiY == null || loY == null) midEl.style.visibility = 'hidden';
      else {
        midEl.style.visibility = 'visible';
        midEl.style.top = `${Math.round((hiY + loY) / 2)}px`;
      }
    }
  }, []);

  /**
   * Place one date label per trading session at the session-open bar (09:15).
   * Collision filter keeps multi-day zoom readable; labels sit below time ticks.
   */
  const positionSessionAxisLabels = useCallback(() => {
    const chart = chartRef.current;
    const overlay = sessionAxisOverlayRef.current;
    if (!chart || !overlay || !intraday) return;
    const marks = sessionAxisMarksRef.current;
    const labels = overlay.querySelectorAll('.ic-session-axis-lbl');
    if (!marks.length || !labels.length) {
      labels.forEach((el) => { el.style.visibility = 'hidden'; });
      return;
    }
    const ts = chart.timeScale();
    const width = overlay.clientWidth || 0;
    const MIN_GAP = 52; // px — avoid overlapping date labels when zoomed out
    let lastX = -Infinity;
    marks.forEach((mk, i) => {
      const el = labels[i];
      if (!el) return;
      const x = ts.timeToCoordinate(mk.time);
      if (x == null || !isFinite(x) || x < 8 || (width > 0 && x > width - 8)) {
        el.style.visibility = 'hidden';
        return;
      }
      if (x - lastX < MIN_GAP) {
        el.style.visibility = 'hidden';
        return;
      }
      el.style.visibility = 'visible';
      el.style.left = `${Math.round(x)}px`;
      lastX = x;
    });
  }, [intraday]);

  const clearFibLines = useCallback(() => {
    const chart = chartRef.current;
    const candle = seriesRef.current;
    // Remove session-scoped LineSeries segments
    if (chart && fibSeriesMapRef.current.size) {
      fibSeriesMapRef.current.forEach((s) => {
        try { chart.removeSeries(s); } catch (_) {}
      });
      fibSeriesMapRef.current.clear();
    }
    // Remove EOD price-line fibs
    if (candle) {
      fibPriceLineRefs.current.forEach((pl) => {
        try { candle.removePriceLine(pl); } catch (_) {}
      });
    }
    fibPriceLineRefs.current = [];
    fibSigRef.current = '';
    fibLevelsRef.current = [];
    fibSessionsRef.current = [];
    fibActiveDayRef.current = null;
    setFibLevels([]);
  }, []);

  const applyUserHlines = useCallback((series, lines, selectedId) => {
    userHlineRefs.current.forEach(({ pl }) => {
      try { series.removePriceLine(pl); } catch (_) {}
    });
    userHlineRefs.current = (lines || []).map((lineDef) => {
      const isSelected = lineDef.id === selectedId;
      const suffix = lineDef.isShared ? ' 🛡️' : '';
      const pl = series.createPriceLine({
        price: lineDef.price,
        color: isSelected ? '#FFFFFF' : lineDef.color || '#3B82F6',
        lineWidth: isSelected ? (lineDef.isShared ? 3 : 2) : (lineDef.isShared ? 2 : 1),
        lineStyle: lineDef.style === 'dashed' ? 2 : 0,
        axisLabelVisible: true,
        title: lineDef.label ? `${lineDef.label}${suffix} (${lineDef.price.toFixed(2)})` : `${lineDef.price.toFixed(2)}${suffix}`,
      });
      return { id: lineDef.id, pl };
    });
  }, []);

  /**
   * Draw Auto Fib:
   * - Intraday: first completed TF candle H/L per session (09:15–15:25 segments).
   * - User override (drag/stretch) replaces H/L for the active day only.
   * - EOD: classic full-width price lines on visible range H/L.
   */
  const applyFibLines = useCallback((series, candles, opts = {}) => {
    const chart = chartRef.current;
    if (!showFib || !candles?.length || !series || !chart) {
      clearFibLines();
      return;
    }

    const force = !!opts.force;

    // ── EOD / daily-range: keep simple price lines ──────────────────────────
    if (!intraday) {
      let hi = -Infinity;
      let lo = Infinity;
      for (const c of candles) {
        if (c.high == null || c.low == null) continue;
        if (c.high > hi) hi = c.high;
        if (c.low < lo) lo = c.low;
      }
      const ov = fibUserOverrideRef.current;
      if (ov && ov.dayKey === 'eod') {
        hi = ov.hi;
        lo = ov.lo;
      }
      const levels = fibLevelsFromHighLow(hi, lo);
      const sig = levels
        ? `eod:${hi}|${lo}|${fibSelectedRef.current ? 1 : 0}|${ov ? 1 : 0}`
        : '';
      if (!force && sig === fibSigRef.current) {
        requestAnimationFrame(positionFibLabels);
        return;
      }
      clearFibLines();
      if (!levels) return;
      fibSigRef.current = sig;
      const selected = fibSelectedRef.current;
      levels.forEach((lv) => {
        const isAnchor = lv.k === '0%' || lv.k === '100%';
        const pl = series.createPriceLine({
          price: lv.price,
          color: lv.color || AUTO_FIB_COLORS[lv.k] || 'rgba(120,144,156,0.7)',
          lineWidth: selected && isAnchor ? 2 : 1,
          lineStyle: 1,
          axisLabelVisible: false,
        });
        fibPriceLineRefs.current.push(pl);
      });
      fibSessionsRef.current = [{
        dayKey: 'eod',
        hi: +Number(hi).toFixed(2),
        lo: +Number(lo).toFixed(2),
        levels,
        frozen: true,
        userAdjusted: !!ov,
      }];
      fibActiveDayRef.current = 'eod';
      fibLevelsRef.current = levels;
      setFibLevels(levels);
      requestAnimationFrame(positionFibLabels);
      return;
    }

    // ── Intraday: first completed TF-candle H/L, session-scoped segments ────
    const MAX_FIB_SESSIONS = 12;
    const mins = intervalMinutesRef.current || intervalMinutes || 5;
    const allSessions = computeSessionScopedAutoFib(candles, { intervalMinutes: mins });
    let sessions = allSessions.length > MAX_FIB_SESSIONS
      ? allSessions.slice(-MAX_FIB_SESSIONS)
      : allSessions;

    // Apply user override to the active (latest) day when present
    const ov = fibUserOverrideRef.current;
    if (ov && sessions.length) {
      sessions = sessions.map((s) => {
        if (s.dayKey !== ov.dayKey) return s;
        return applyUserFibHighLow(s, ov.hi, ov.lo) || s;
      });
    }

    const selected = fibSelectedRef.current;
    const sig = `${sessionFibSignature(sessions)}|sel:${selected ? 1 : 0}`;
    if (!force && sig === fibSigRef.current) {
      fibSessionsRef.current = sessions;
      requestAnimationFrame(positionFibLabels);
      return;
    }

    const nextKeys = new Set();
    const map = fibSeriesMapRef.current;

    if (fibPriceLineRefs.current.length) {
      fibPriceLineRefs.current.forEach((pl) => {
        try { series.removePriceLine(pl); } catch (_) {}
      });
      fibPriceLineRefs.current = [];
    }

    const lineOpts = (color, width) => ({
      color,
      lineWidth: width,
      lineStyle: 2, // dashed
      priceScaleId: 'right',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });

    const levelColor = (lv, frozen, isActive) => {
      const base = lv.color || AUTO_FIB_COLORS[lv.k] || 'rgba(120,144,156,0.7)';
      if (selected && isActive) return base;
      if (!frozen || !base.startsWith('rgba(')) return base;
      return base.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/, (_, r, g, b, a) => {
        const nextA = Math.max(0.35, Number(a) * 0.72);
        return `rgba(${r},${g},${b},${nextA})`;
      });
    };

    const latestKey = sessions.length ? sessions[sessions.length - 1].dayKey : null;

    for (const sess of sessions) {
      const isActive = sess.dayKey === latestKey;
      for (const lv of sess.levels) {
        const key = `${sess.dayKey}|${lv.k}`;
        nextKeys.add(key);
        const color = levelColor(lv, sess.frozen, isActive);
        const isAnchor = lv.k === '0%' || lv.k === '100%';
        const width = selected && isActive && isAnchor ? 2 : 1;
        const pts = [
          { time: sess.startTime, value: lv.price },
          { time: sess.endTime, value: lv.price },
        ];
        let line = map.get(key);
        if (!line) {
          line = chart.addSeries(LineSeries, lineOpts(color, width));
          map.set(key, line);
        }
        try {
          line.setData(pts);
          line.applyOptions({ color, lineWidth: width });
        } catch (_) {
          try { chart.removeSeries(line); } catch (__) {}
          line = chart.addSeries(LineSeries, lineOpts(color, width));
          try { line.setData(pts); } catch (___) {}
          map.set(key, line);
        }
      }
    }

    for (const [key, line] of map) {
      if (!nextKeys.has(key)) {
        try { chart.removeSeries(line); } catch (_) {}
        map.delete(key);
      }
    }

    fibSigRef.current = sig;
    fibSessionsRef.current = sessions;
    fibActiveDayRef.current = latestKey;

    const latest = sessions.length ? sessions[sessions.length - 1] : null;
    const labelLevels = latest ? latest.levels : [];
    fibLevelsRef.current = labelLevels;
    setFibLevels(labelLevels);
    requestAnimationFrame(positionFibLabels);
  }, [showFib, intraday, intervalMinutes, clearFibLines, positionFibLabels]);

  const resetFibOverride = useCallback(() => {
    fibUserOverrideRef.current = null;
    saveFibOverride(chartIdRef.current, tfRef.current, null);
    setFibUserAdjusted(false);
    fibSigRef.current = '';
    const series = seriesRef.current;
    if (series && dataRef.current?.length) {
      applyFibLines(series, dataRef.current, { force: true });
    }
  }, [applyFibLines]);

  /** Hit-test fib levels / handles under a pointer Y (container coords). */
  const hitTestFib = useCallback((clientY, clientX) => {
    const series = seriesRef.current;
    const box = chartBoxRef.current;
    if (!series || !box || !showFib) return null;
    const rect = box.getBoundingClientRect();
    const y = clientY - rect.top;
    const x = clientX - rect.left;
    // Ignore clicks on right price scale (~52px)
    if (x > rect.width - 48) return null;

    const active = fibSessionsRef.current.length
      ? fibSessionsRef.current[fibSessionsRef.current.length - 1]
      : null;
    if (!active?.levels?.length) return null;

    // Prefer high/low handles when already selected
    if (fibSelectedRef.current) {
      const hiY = series.priceToCoordinate(active.hi);
      const loY = series.priceToCoordinate(active.lo);
      if (hiY != null && Math.abs(y - hiY) <= FIB_HANDLE_PX) {
        return { kind: 'handle-hi', session: active };
      }
      if (loY != null && Math.abs(y - loY) <= FIB_HANDLE_PX) {
        return { kind: 'handle-lo', session: active };
      }
    }

    let best = null;
    let bestDist = FIB_HIT_PX;
    for (const lv of active.levels) {
      const ly = series.priceToCoordinate(lv.price);
      if (ly == null) continue;
      const dist = Math.abs(y - ly);
      if (dist <= bestDist) {
        bestDist = dist;
        best = { kind: 'level', level: lv, session: active };
      }
    }
    return best;
  }, [showFib]);

  useEffect(() => {
    onRangeChangeRef.current = onLogicalRangeChange;
  }, [onLogicalRangeChange]);

  useEffect(() => {
    syncGroupIdRef.current = syncGroupId;
  }, [syncGroupId]);

  useEffect(() => {
    chartIdRef.current = chartId;
  }, [chartId]);

  useEffect(() => {
    hlineDrawRef.current = hlineDraw;
  }, [hlineDraw]);

  useEffect(() => {
    userHlinesRef.current = userHlines;
  }, [userHlines]);

  useEffect(() => {
    try {
      localStorage.setItem(`tmr_board_hlines_${chartId}`, JSON.stringify(userHlines));
    } catch (_) {}
  }, [userHlines, chartId]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    applyUserHlines(series, allLines, selectedLineId);
  }, [allLines, applyUserHlines, selectedLineId]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key.toLowerCase() === 'h') {
        setHlineDraw(v => !v);
        setSelectedLineId(null);
        setFibSelected(false);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedLineId) {
          const line = allLinesRef.current.find(l => l.id === selectedLineId);
          if (line?.isShared) {
            if (user?.role !== 'admin') return;
            fetch('/api/api_shared_hlines.php', { method: 'POST', body: JSON.stringify({ action: 'delete', id: selectedLineId }) });
            setSharedHlines(prev => prev.filter(l => l.id !== selectedLineId));
          } else {
            setUserHlines(prev => prev.filter(l => l.id !== selectedLineId));
          }
          setSelectedLineId(null);
        } else if (fibSelectedRef.current && fibUserOverrideRef.current) {
          // Delete on selected user-adjusted fib → reset to auto first-candle
          resetFibOverride();
          setFibSelected(false);
        }
      } else if (e.key === 'Escape') {
        setHlineDraw(false);
        setSelectedLineId(null);
        setFibSelected(false);
        fibSigRef.current = '';
        const series = seriesRef.current;
        if (series && dataRef.current?.length && showFib) {
          applyFibLines(series, dataRef.current, { force: true });
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedLineId, resetFibOverride, applyFibLines, showFib]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    if (!showFib) {
      clearFibLines();
      setFibSelected(false);
    } else if (data.length) {
      applyFibLines(series, data, { force: true });
    }
  }, [showFib, data, intervalMinutes, clearFibLines, applyFibLines]);

  useEffect(() => {
    if (!fibLevels.length) return;
    requestAnimationFrame(positionFibLabels);
  }, [fibLevels, positionFibLabels, fibSelected]);

  // ── Interactive Auto Fib: select, drag whole, stretch high/low ───────────
  useEffect(() => {
    const box = chartBoxRef.current;
    if (!box || !showFib) return;

    const priceFromEvent = (e) => {
      const series = seriesRef.current;
      if (!series) return null;
      const rect = box.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const price = series.coordinateToPrice(y);
      return (price == null || !isFinite(price)) ? null : price;
    };

    const onPointerDown = (e) => {
      if (e.button != null && e.button !== 0) return;
      if (hlineDrawRef.current) return;
      // Let toolbar buttons work
      if (e.target?.closest?.('.ic-fib-toolbar, .ic-hline-toolbar, .ic-mini-tgl, button, input')) return;

      const hit = hitTestFib(e.clientY, e.clientX);
      if (!hit) {
        if (fibSelectedRef.current) {
          setFibSelected(false);
          fibSigRef.current = '';
          const series = seriesRef.current;
          if (series && dataRef.current?.length) applyFibLines(series, dataRef.current, { force: true });
        }
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      setSelectedLineId(null);

      const session = hit.session;
      if (!fibSelectedRef.current) {
        setFibSelected(true);
        fibSigRef.current = '';
        const series = seriesRef.current;
        if (series && dataRef.current?.length) applyFibLines(series, dataRef.current, { force: true });
      }

      let mode = 'drag';
      if (hit.kind === 'handle-hi') mode = 'stretch-hi';
      else if (hit.kind === 'handle-lo') mode = 'stretch-lo';

      fibDragRef.current = {
        mode,
        startY: e.clientY,
        startPrice: priceFromEvent(e),
        startHi: session.hi,
        startLo: session.lo,
        dayKey: session.dayKey,
        pointerId: e.pointerId,
      };

      try { box.setPointerCapture(e.pointerId); } catch (_) {}
      box.classList.add(mode === 'drag' ? 'ic-fib-dragging' : 'ic-fib-stretching');
    };

    const onPointerMove = (e) => {
      const drag = fibDragRef.current;
      if (!drag) {
        // Cursor feedback when hovering fib
        const hit = hitTestFib(e.clientY, e.clientX);
        if (hit?.kind === 'handle-hi' || hit?.kind === 'handle-lo') {
          box.style.cursor = 'ns-resize';
        } else if (hit) {
          box.style.cursor = fibSelectedRef.current ? 'grab' : 'pointer';
        } else if (!hlineDrawRef.current) {
          box.style.cursor = '';
        }
        return;
      }

      const price = priceFromEvent(e);
      if (price == null || drag.startPrice == null) return;

      let hi = drag.startHi;
      let lo = drag.startLo;
      if (drag.mode === 'drag') {
        const delta = price - drag.startPrice;
        hi = drag.startHi + delta;
        lo = drag.startLo + delta;
      } else if (drag.mode === 'stretch-hi') {
        hi = price;
        lo = drag.startLo;
        if (hi < lo) { const t = hi; hi = lo; lo = t; }
      } else if (drag.mode === 'stretch-lo') {
        hi = drag.startHi;
        lo = price;
        if (hi < lo) { const t = hi; hi = lo; lo = t; }
      }

      // Live update without waiting for pointerup
      const next = {
        dayKey: drag.dayKey,
        hi: +Number(hi).toFixed(2),
        lo: +Number(lo).toFixed(2),
      };
      if (next.hi === next.lo) return;
      fibUserOverrideRef.current = next;
      setFibUserAdjusted(true);
      fibSigRef.current = '';
      const series = seriesRef.current;
      if (series && dataRef.current?.length) {
        applyFibLines(series, dataRef.current, { force: true });
      }
    };

    const endDrag = (e) => {
      const drag = fibDragRef.current;
      if (!drag) return;
      fibDragRef.current = null;
      box.classList.remove('ic-fib-dragging', 'ic-fib-stretching');
      try {
        if (e?.pointerId != null) box.releasePointerCapture(e.pointerId);
      } catch (_) {}
      const ov = fibUserOverrideRef.current;
      if (ov) {
        saveFibOverride(chartIdRef.current, tfRef.current, ov);
        setFibUserAdjusted(true);
      }
      box.style.cursor = fibSelectedRef.current ? 'grab' : '';
    };

    box.addEventListener('pointerdown', onPointerDown);
    box.addEventListener('pointermove', onPointerMove);
    box.addEventListener('pointerup', endDrag);
    box.addEventListener('pointercancel', endDrag);

    return () => {
      box.removeEventListener('pointerdown', onPointerDown);
      box.removeEventListener('pointermove', onPointerMove);
      box.removeEventListener('pointerup', endDrag);
      box.removeEventListener('pointercancel', endDrag);
      box.classList.remove('ic-fib-dragging', 'ic-fib-stretching');
      box.style.cursor = '';
    };
  }, [showFib, hitTestFib, applyFibLines]);

  const beginSync = () => {
    syncingRef.current = true;
  };

  const endSync = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncingRef.current = false;
      });
    });
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    seriesStateRef.current = { len: 0, lastTime: null, lastPoint: null, didFit: false };
    fibPriceLineRefs.current = [];
    fibSeriesMapRef.current.clear();
    userHlineRefs.current = [];
    fibSigRef.current = '';
    setChartError(null);

    const chartH = Math.max(fillHeight ? (el.clientHeight || height || 280) : (height || 280), 120);
    // Defer one frame if container has zero width (layout race on first paint)
    const width = Math.max(el.clientWidth || 0, 40);

    // Read theme-aware colors at chart creation time (light + dark via CSS vars)
    const candleGreen = getCSSVar('--candle-green', '#00FF66');
    const candleRed   = getCSSVar('--candle-red', '#FF0055');
    const chartGrid   = getCSSVar('--chart-grid', 'rgba(255,255,255,0.05)');
    const chartText   = getCSSVar('--chart-text', 'rgba(255,255,255,0.45)');
    const borderCol   = getCSSVar('--border-color', 'rgba(255,255,255,0.12)');

    let chart;
    try {
      chart = createChart(el, {
        width,
        height: chartH,
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: chartText,
          attributionLogo: false,
        },
        grid: {
          vertLines: { color: chartGrid },
          horzLines: { color: chartGrid },
        },
        crosshair: { mode: CrosshairMode.Normal },
        rightPriceScale: {
          borderColor: borderCol,
          autoScale: true,
        },
        ...CHART_INTERACTION_OPTS,
        timeScale: {
          borderColor: borderCol,
          timeVisible: intraday,
          secondsVisible: false,
          ...CHART_TIME_SCALE_OPTS,
          rightOffset: 4,
          // Allow "15 Jul 09:15" (12 chars); LWC default max is 8 and truncates dates
          tickMarkMaxCharacterLength: 14,
          allowBoldLabels: true,
          // Extra room so session-date overlay sits cleanly under time ticks
          ...(intraday ? { minimumHeight: 32 } : {}),
          ...(intraday ? {
            // Smart axis: time-only on LWC ticks; session dates render in DOM overlay below
            tickMarkFormatter: (timestamp, tickMarkType) => {
              if (typeof timestamp !== 'number') return String(timestamp);
              return formatChartTickMark(timestamp, tickMarkType, {
                sessionOpenTimes: sessionOpenTimesRef.current,
                // Overlay owns the date row so we don't double-print at 09:15
                sessionDateOnTick: false,
              });
            },
          } : {}),
        },
        ...(intraday ? {
          localization: {
            // Crosshair / legend: always "14 Jul 14:30"
            timeFormatter: (timestamp) => {
              if (typeof timestamp !== 'number') return String(timestamp);
              return formatChartDateTime(timestamp);
            },
          },
        } : {}),
      });
    } catch (err) {
      try { console.error('[TMR] createChart failed', chartId, err); } catch (_) {}
      setChartError(String(err?.message || err || 'Chart init failed'));
      return undefined;
    }
    chartRef.current = chart;
    const detachInteraction = attachChartInteraction(chart, el, {
      zoomStep: 0.14,
      fineZoomStep: 0.06,
      panBars: 4,
      // Mini boards: avoid stealing global keys while typing elsewhere
      enableKeyboard: false,
    });

    let series;
    try {
      series = chart.addSeries(CandlestickSeries, {
        upColor: candleGreen,
        downColor: candleRed,
        borderUpColor: candleGreen,
        borderDownColor: candleRed,
        wickUpColor: candleGreen,
        wickDownColor: candleRed,
      });
      seriesRef.current = series;
      chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.08, bottom: 0.08 } });
    } catch (err) {
      try { console.error('[TMR] addSeries failed', chartId, err); } catch (_) {}
      setChartError(String(err?.message || err || 'Series init failed'));
      try { detachInteraction(); } catch (_) {}
      try { chart.remove(); } catch (_) {}
      chartRef.current = null;
      return undefined;
    }

    const onRange = (range) => {
      // Always keep fib + smart session-date overlays aligned with the viewport
      requestAnimationFrame(() => {
        positionFibLabels();
        positionSessionAxisLabels();
      });
      if (!range) return;

      // Ignore range events we ourselves applied while following a peer
      if (syncingRef.current) return;
      const gid = syncGroupIdRef.current;
      if (gid) {
        const group = getChartSyncGroup(gid);
        if (group.isApplying) return;
        group.onLogicalRangeChange(chartIdRef.current, range);
      }

      // Optional React parent callback (legacy board path)
      if (onRangeChangeRef.current) {
        onRangeChangeRef.current(chartIdRef.current, range);
      }
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRange);

    // Join linked-chart group so zoom/pan on any peer stays in lockstep
    if (syncGroupId) {
      getChartSyncGroup(syncGroupId).register(chartId, chart);
    }

    const onChartClick = (param) => {
      if (!param.point) return;
      const series = seriesRef.current;

      if (!hlineDrawRef.current) {
        // Prefer fib hit when interactive fib is enabled (handled via pointer on box).
        // Still select horizontal lines when fib is not under cursor.
        let nearest = null;
        let minDistance = 12;
        allLinesRef.current.forEach(line => {
          const y = series.priceToCoordinate(line.price);
          if (y !== null) {
            const dist = Math.abs(y - param.point.y);
            if (dist < minDistance) {
              minDistance = dist;
              nearest = line;
            }
          }
        });
        if (nearest) {
          setSelectedLineId(nearest.id);
          setFibSelected(false);
        }
        return;
      }

      const price = series.coordinateToPrice(param.point.y);
      if (price == null || !isFinite(price)) return;
      const rounded = +Number(price).toFixed(2);

      const newLine = {
        id: 'hl_' + Math.random().toString(36).substr(2, 9),
        price: rounded,
        color: '#3B82F6',
        style: 'solid',
        label: '',
      };

      setUserHlines(prev => [...prev, newLine]);
      setSelectedLineId(newLine.id);
      setFibSelected(false);
      setHlineDraw(false);
    };
    chart.subscribeClick(onChartClick);
    applyUserHlines(series, allLinesRef.current, selectedLineId);

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry || !chartRef.current) return;
      const { width, height: h } = entry.contentRect;
      const nextH = fillHeight ? Math.max(Math.floor(h), 120) : chartH;
      chartRef.current.applyOptions({ width: Math.floor(width), height: nextH });
      requestAnimationFrame(() => {
        positionFibLabels();
        positionSessionAxisLabels();
      });
    });
    ro.observe(el);

    // Live theme swap: re-apply token colors without full chart remount
    const applyThemeColors = () => {
      if (!chartRef.current || !seriesRef.current) return;
      const g = getCSSVar('--candle-green', '#00FF66');
      const r = getCSSVar('--candle-red', '#FF0055');
      const grid = getCSSVar('--chart-grid', 'rgba(255,255,255,0.05)');
      const text = getCSSVar('--chart-text', 'rgba(255,255,255,0.45)');
      const border = getCSSVar('--border-color', 'rgba(255,255,255,0.12)');
      try {
        chartRef.current.applyOptions({
          layout: { textColor: text },
          grid: {
            vertLines: { color: grid },
            horzLines: { color: grid },
          },
          rightPriceScale: { borderColor: border },
          timeScale: { borderColor: border },
        });
        seriesRef.current.applyOptions({
          upColor: g,
          downColor: r,
          borderUpColor: g,
          borderDownColor: r,
          wickUpColor: g,
          wickDownColor: r,
        });
      } catch (_) {}
    };
    const themeMo = typeof MutationObserver !== 'undefined'
      ? new MutationObserver((muts) => {
          if (muts.some((m) => m.attributeName === 'data-theme')) applyThemeColors();
        })
      : null;
    if (themeMo) themeMo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      try { detachInteraction(); } catch (_) {}
      chart.unsubscribeClick(onChartClick);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRange);
      if (syncGroupId) {
        try {
          getChartSyncGroup(syncGroupId).unregister(chartId);
        } catch (_) {}
      }
      ro.disconnect();
      if (themeMo) themeMo.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      seriesRef.current = null;
      fibPriceLineRefs.current = [];
      fibSeriesMapRef.current.clear();
      userHlineRefs.current = [];
      fibSigRef.current = '';
      sessionOpenTimesRef.current = new Set();
      sessionAxisMarksRef.current = [];
      seriesStateRef.current = { len: 0, lastTime: null, lastPoint: null, didFit: false };
    };
  }, [height, fillHeight, chartId, syncGroupId, intraday, chartEpoch, positionFibLabels, positionSessionAxisLabels, applyUserHlines]);

  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || !data.length) return;

    // Lightweight Charts: series.update() for forming/next bar; setData only on resync
    const points = data.map(toLwcPoint).filter(Boolean);
    const last = data[data.length - 1];
    const prev = data.length > 1 ? data[data.length - 2] : last;
    const lastClose = Number(last?.close);
    const prevClose = Number(prev?.close);
    const chg = Number.isFinite(lastClose) && Number.isFinite(prevClose) ? lastClose - prevClose : 0;
    const chgP = prevClose ? ((chg / prevClose) * 100).toFixed(2) : '0.00';
    setHeader({
      last: Number.isFinite(lastClose) ? lastClose : 0,
      chg: Number.isFinite(chg) ? chg : 0,
      chgP,
      bull: chg >= 0,
    });

    try {
      applySeriesPoints(series, points, seriesStateRef.current, {
        live,
        chart,
      });
    } catch (err) {
      try { console.warn('[TMR] series apply failed', chartId, err); } catch (_) {}
    }

    try {
      applyFibLines(series, data);
    } catch (err) {
      try { console.warn('[TMR] fib apply failed', chartId, err); } catch (_) {}
    }

    // Keep session-open set in sync for tickMarkFormatter + date overlay
    if (intraday) {
      const opens = sessionOpenTimeSet(data);
      sessionOpenTimesRef.current = opens;
      const marks = collectSessionOpenAxisMarks(data);
      sessionAxisMarksRef.current = marks;
      setSessionAxisMarks(marks);
      requestAnimationFrame(positionSessionAxisLabels);
    } else if (sessionAxisMarksRef.current.length) {
      sessionOpenTimesRef.current = new Set();
      sessionAxisMarksRef.current = [];
      setSessionAxisMarks([]);
    }
  }, [data, live, applyFibLines, intraday, positionSessionAxisLabels]);

  useEffect(() => {
    if (!intraday || !sessionAxisMarks.length) return;
    requestAnimationFrame(positionSessionAxisLabels);
  }, [sessionAxisMarks, positionSessionAxisLabels, intraday]);

  // Legacy React-prop sync path (used when parent drives logicalRange without syncGroupId)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !logicalRange) return;
    // Prefer the group path when registered — avoid double-applying
    if (syncGroupIdRef.current) return;

    const current = chart.timeScale().getVisibleLogicalRange();
    if (rangesEqual(current, logicalRange)) return;

    beginSync();
    try {
      chart.timeScale().setVisibleLogicalRange(logicalRange);
    } catch (_) {
      /* range may be outside series bounds during load races */
    }
    endSync();
    requestAnimationFrame(() => {
      positionFibLabels();
      positionSessionAxisLabels();
    });
  }, [logicalRange, positionFibLabels, positionSessionAxisLabels]);

  const chartStyle = fillHeight
    ? { width: '100%' }
    : { width: '100%', height };

  if (!data.length) {
    return (
      <div className={`ic-mini-wrap${fillHeight ? ' ic-mini-fill' : ''}`}>
        <div className="ic-mini-empty" style={fillHeight ? { flex: 1 } : { height }}>
          No data for {label || 'symbol'}
        </div>
      </div>
    );
  }

  const lastDisp = Number.isFinite(+header.last) ? (+header.last).toFixed(2) : '—';
  const chgDisp = Number.isFinite(+header.chg) ? (+header.chg).toFixed(2) : '0.00';
  const chgPDisp = header.chgP != null ? String(header.chgP) : '0.00';

  return (
    <div className={`ic-mini-wrap${fillHeight ? ' ic-mini-fill' : ''}`}>
      <div className="ic-mini-hdr">
        <span className="ic-mini-sym">{label}</span>
        <span className={`ic-mini-last ${header.bull ? 'sg' : 'sr'}`}>{lastDisp}</span>
        <span className={`ic-mini-chg ${header.bull ? 'sg' : 'sr'}`}>
          {header.bull ? '+' : ''}{chgDisp} ({chgPDisp}%)
        </span>
        <div className="ic-mini-tools">
          <button
            type="button"
            className={`ic-mini-tgl${hlineDraw ? ' on' : ''}`}
            title="Click chart to plot horizontal line"
            onClick={() => {
              setHlineDraw(v => !v);
              setFibSelected(false);
            }}
          >
            H-LINE
          </button>
          {userHlines.length > 0 && (
            <button
              type="button"
              className="ic-mini-tgl ic-mini-tgl-clear"
              title={`Clear ${userHlines.length} horizontal line(s)`}
              onClick={() => setUserHlines([])}
            >
              ×{userHlines.length}
            </button>
          )}
          {showFib && fibUserAdjusted && (
            <button
              type="button"
              className="ic-mini-tgl ic-mini-tgl-fib-reset"
              title="Reset Auto Fib to first completed candle of this timeframe"
              onClick={resetFibOverride}
            >
              FIB↺
            </button>
          )}
          {/* Far-right data source — never reconnecting/polling connection chrome */}
          {(dataSource || live || intraday) && (
            <DataSourceBadge
              resolved={dataSource || undefined}
              intraday={intraday}
              mode={live ? 'live' : (intraday ? 'historical' : 'historical')}
              className="ic-ds-badge-chart"
            />
          )}
        </div>
      </div>
      <div
        ref={chartBoxRef}
        className={[
          'ic-mini-chart-box',
          hlineDraw ? 'ic-hline-draw' : '',
          fibSelected ? 'ic-fib-selected' : '',
          showFib && fibLevels.length ? 'ic-fib-interactive' : '',
        ].filter(Boolean).join(' ')}
        style={chartStyle}
      >
        {chartError && (
          <div
            className="ic-mini-empty"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 6,
              background: 'var(--chart-bg, rgba(10,10,12,0.92))',
            }}
          >
            Chart error — {chartError}
            <button
              type="button"
              className="ic-mini-tgl"
              style={{ marginLeft: 8 }}
              onClick={() => {
                setChartError(null);
                setChartEpoch((n) => n + 1);
              }}
            >
              Retry
            </button>
          </div>
        )}
        <div ref={containerRef} className="ic-mini-chart" />

        {selectedLineId && allLines.find(l => l.id === selectedLineId) && (
          <HLineToolbar
            line={allLines.find(l => l.id === selectedLineId)}
            isAdmin={user?.role === 'admin'}
            onUpdate={(updatedLine) => {
              if (updatedLine.isShared) {
                if (user?.role !== 'admin') return;
                fetch('/api/api_shared_hlines.php', { method: 'POST', body: JSON.stringify({ action: 'upsert', ...updatedLine }) });
                setSharedHlines(prev => prev.map(l => l.id === updatedLine.id ? updatedLine : l));
              } else {
                setUserHlines(prev => prev.map(l => l.id === updatedLine.id ? updatedLine : l));
              }
            }}
            onDelete={(id) => {
              const line = allLinesRef.current.find(l => l.id === id);
              if (line?.isShared) {
                if (user?.role !== 'admin') {
                  alert('Only Admin can delete shared lines');
                  return;
                }
                fetch('/api/api_shared_hlines.php', { method: 'POST', body: JSON.stringify({ action: 'delete', id }) });
                setSharedHlines(prev => prev.filter(l => l.id !== id));
              } else {
                setUserHlines(prev => prev.filter(l => l.id !== id));
              }
              setSelectedLineId(null);
            }}
            onShare={(id) => {
              const line = userHlinesRef.current.find(l => l.id === id);
              if (line && user?.role === 'admin') {
                const shared = { ...line, isShared: true, chart_id: chartId };
                fetch('/api/api_shared_hlines.php', { method: 'POST', body: JSON.stringify({ action: 'upsert', ...shared }) });
                setUserHlines(prev => prev.filter(l => l.id !== id));
                setSharedHlines(prev => [...prev, shared]);
              }
            }}
            onClose={() => setSelectedLineId(null)}
          />
        )}

        {showFib && fibSelected && fibLevels.length > 0 && (
          <div className="ic-fib-toolbar" role="toolbar" aria-label="Auto Fib controls">
            <span className="ic-fib-toolbar-label">
              AutoFib{fibUserAdjusted ? ' · custom' : ' · auto'}
            </span>
            <span className="ic-fib-toolbar-hint">drag · stretch handles</span>
            {fibUserAdjusted && (
              <button type="button" className="ic-fib-toolbar-btn" onClick={resetFibOverride} title="Reset to first TF candle">
                Reset
              </button>
            )}
            <button
              type="button"
              className="ic-fib-toolbar-btn"
              onClick={() => {
                setFibSelected(false);
                fibSigRef.current = '';
                const series = seriesRef.current;
                if (series && dataRef.current?.length) applyFibLines(series, dataRef.current, { force: true });
              }}
              title="Deselect"
            >
              ✕
            </button>
          </div>
        )}

        {showFib && fibLevels.length > 0 && (
          <div ref={fibOverlayRef} className={`ic-mini-fib-labels${fibSelected ? ' selected' : ''}`} aria-hidden="true">
            {fibLevels.map(lv => (
              <span
                key={lv.k || lv.key}
                className="ic-mini-fib-lbl"
                style={{ color: lv.color || AUTO_FIB_COLORS[lv.k || lv.key] || '#78909c' }}
              >
                {lv.k || lv.key}
              </span>
            ))}
          </div>
        )}

        {/* Interactive high / low stretch handles (active session only) */}
        {showFib && fibSelected && fibLevels.length > 0 && (
          <div ref={fibHandlesRef} className="ic-fib-handles" aria-hidden="true">
            <div className="ic-fib-handle ic-fib-handle-hi" title="Drag to stretch High (0%)" />
            <div className="ic-fib-handle ic-fib-handle-lo" title="Drag to stretch Low (100%)" />
            <div className="ic-fib-handle ic-fib-handle-mid" title="Drag to move entire Fib" />
          </div>
        )}

        {/* Session date labels — one per trading day, under the time axis */}
        {intraday && sessionAxisMarks.length > 0 && (
          <div
            ref={sessionAxisOverlayRef}
            className="ic-session-axis-labels"
            aria-hidden="true"
          >
            {sessionAxisMarks.map((mk) => (
              <span
                key={mk.dayKey}
                className="ic-session-axis-lbl"
                title={`Session open ${mk.dayKey} 09:15`}
              >
                {mk.shortLabel}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}